import { useEffect, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Group,
  Loader,
  Modal,
  Pagination,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertTriangle,
  IconMailbox,
  IconMailOff,
  IconMailPlus,
  IconSearch,
  IconTrash,
} from '@tabler/icons-react';
import { PageHeader } from '@/components/page-header';
import { StatTile } from '@/components/stat-tile';
import { ApiError, NetworkError } from '@/lib/api-error';
import {
  deleteWaitlistSignup,
  getWaitlistSummary,
  listWaitlist,
  setWaitlistUnsubscribed,
  type SignupPage,
  type WaitlistFilter,
  type WaitlistSignup,
  type WaitlistSummary,
} from '@/lib/waitlist';
import classes from './waitlist-page.module.css';

const PAGE_SIZE = 25;

const date = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});
const format = new Intl.NumberFormat('en-GB');

function describeError(error: unknown): string {
  if (error instanceof NetworkError) {
    return 'The API did not answer. It may not be running.';
  }
  if (error instanceof ApiError) return error.message;
  return 'Something went wrong.';
}

/** A date, or an em dash. Never an empty cell, which reads as a broken render. */
function on(value: string | null): string {
  return value ? date.format(new Date(value)) : '—';
}

const FILTERS: { value: WaitlistFilter; label: string }[] = [
  { value: 'subscribed', label: 'Subscribed' },
  { value: 'unsubscribed', label: 'Unsubscribed' },
  { value: 'notified', label: 'Notified' },
  { value: 'all', label: 'All' },
];

interface ConfirmProps {
  signup: WaitlistSignup;
  onClose: () => void;
  onDone: (signup: WaitlistSignup) => void;
}

/**
 * Confirmation before a delete.
 *
 * The body states the concrete consequence, as every confirmation in this
 * portal does — and here the consequence has a part that is genuinely easy to
 * get wrong, so it is spelled out rather than implied.
 */
function RemoveForm({ signup, onClose, onDone }: ConfirmProps) {
  const [error, setError] = useState<unknown>(null);
  const [isRemoving, setIsRemoving] = useState(false);

  async function confirm() {
    setIsRemoving(true);
    try {
      await deleteWaitlistSignup(signup.id);
      onDone(signup);
      onClose();
    } catch (caught) {
      setError(caught);
    } finally {
      setIsRemoving(false);
    }
  }

  return (
    <Stack gap="md">
      {error !== null && (
        <Alert color="red" icon={<IconAlertTriangle size={18} />}>
          <Text size="sm">{describeError(error)}</Text>
        </Alert>
      )}

      <Text size="sm">
        {signup.email} is deleted from the waitlist and will not get the launch
        email. There is no undo — if this address is typed into the site&rsquo;s
        form again it comes back as a new signup with a new join date.
      </Text>
      <Text size="sm">
        The address, and your name, are written to the admin audit log. So
        removing the row does not remove the address from this database.
      </Text>

      <Group justify="flex-end" gap="sm">
        <Button variant="subtle" color="gray" onClick={onClose} disabled={isRemoving}>
          Cancel
        </Button>
        <Button color="red" loading={isRemoving} onClick={() => void confirm()}>
          Remove
        </Button>
      </Group>
    </Stack>
  );
}

/** Confirmation before flipping the unsubscribe flag, either way. */
function ConsentForm({ signup, onClose, onDone }: ConfirmProps) {
  const [error, setError] = useState<unknown>(null);
  const [isSaving, setIsSaving] = useState(false);

  const unsubscribing = signup.unsubscribedAt === null;

  async function confirm() {
    setIsSaving(true);
    try {
      const saved = await setWaitlistUnsubscribed(signup.id, unsubscribing);
      onDone(saved);
      onClose();
    } catch (caught) {
      setError(caught);
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Stack gap="md">
      {error !== null && (
        <Alert color="red" icon={<IconAlertTriangle size={18} />}>
          <Text size="sm">{describeError(error)}</Text>
        </Alert>
      )}

      {unsubscribing ? (
        <>
          <Text size="sm">
            {signup.email} stays on the list and is skipped by the launch email.
            Nothing is deleted.
          </Text>
          <Text size="sm">
            It is not permanent either: while the confirmation email is off,
            anyone typing this address into the site&rsquo;s form clears it
            again. Recorded in the admin audit log against your account.
          </Text>
        </>
      ) : (
        <>
          <Text size="sm">
            {signup.email} goes back on the launch list after asking to be taken
            off it.
          </Text>
          <Text size="sm">
            Nothing here records that they asked to come back — do this only if
            they asked you directly. Recorded in the admin audit log against your
            account.
          </Text>
        </>
      )}

      <Group justify="flex-end" gap="sm">
        <Button variant="subtle" color="gray" onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          color={unsubscribing ? 'brand' : 'brand'}
          loading={isSaving}
          onClick={() => void confirm()}
        >
          {unsubscribing ? 'Mark unsubscribed' : 'Put back on the list'}
        </Button>
      </Group>
    </Stack>
  );
}

export function WaitlistPage() {
  const [data, setData] = useState<SignupPage | null>(null);
  const [summary, setSummary] = useState<WaitlistSummary | null>(null);
  const [status, setStatus] = useState<WaitlistFilter>('subscribed');
  const [page, setPage] = useState(1);
  // Held apart from the term the query uses, so typing does not fire a request
  // per keystroke. The search runs on submit.
  const [term, setTerm] = useState('');
  const [query, setQuery] = useState('');
  const [loadError, setLoadError] = useState<unknown>(null);
  const [isLoading, setIsLoading] = useState(true);

  // What each dialog is about, and whether it is open, held separately.
  // Clearing the subject on close would empty the dialog while it is still
  // fading out; leaving it set until the next open keeps a close looking like a
  // close.
  const [removing, setRemoving] = useState<WaitlistSignup | null>(null);
  const [isRemoveOpen, setRemoveOpen] = useState(false);
  const [consenting, setConsenting] = useState<WaitlistSignup | null>(null);
  const [isConsentOpen, setConsentOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setIsLoading(true);
      try {
        const result = await listWaitlist({
          status,
          q: query,
          page,
          pageSize: PAGE_SIZE,
        });
        if (cancelled) return;
        setData(result);
        setLoadError(null);
      } catch (caught) {
        if (!cancelled) setLoadError(caught);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    void load();

    return () => {
      cancelled = true;
    };
  }, [status, query, page]);

  // Fetched separately and allowed to fail on its own, the way the voice list is
  // on the languages screen. A failed count leaves the tiles showing an em dash
  // and the table still works.
  //
  // That em dash is NOT the dashboard's "Not built" case, which means no data
  // model exists. Here the model exists and one request failed, and conflating
  // the two would undo a distinction docs/analytics.md insists on.
  useEffect(() => {
    let cancelled = false;
    void refreshSummary(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, []);

  async function refreshSummary(isCancelled: () => boolean = () => false) {
    try {
      const result = await getWaitlistSummary();
      if (!isCancelled()) setSummary(result);
    } catch {
      if (!isCancelled()) setSummary(null);
    }
  }

  const signups = data?.signups ?? [];
  const total = data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  function openRemove(signup: WaitlistSignup) {
    setRemoving(signup);
    setRemoveOpen(true);
  }

  function openConsent(signup: WaitlistSignup) {
    setConsenting(signup);
    setConsentOpen(true);
  }

  function onRemoved(removed: WaitlistSignup) {
    setData((current) =>
      current === null
        ? current
        : {
            ...current,
            signups: current.signups.filter((item) => item.id !== removed.id),
            total: Math.max(0, current.total - 1),
          },
    );
    // The "On the list" tile is now wrong by one, and a stat quietly stale is
    // worse than one that flickers.
    void refreshSummary();
    notifications.show({ color: 'gray', message: `${removed.email} removed.` });
  }

  function onConsentChanged(saved: WaitlistSignup) {
    // Spliced in place and deliberately left visible even when it no longer
    // matches the active filter: a row vanishing out from under the click reads
    // as a bug. It will be gone on the next load, which is honest.
    //
    // No re-sort comparator, unlike the settings screens: the server orders by
    // created_at and neither write touches it.
    setData((current) =>
      current === null
        ? current
        : {
            ...current,
            signups: current.signups.map((item) =>
              item.id === saved.id ? saved : item,
            ),
          },
    );
    notifications.show({
      color: saved.unsubscribedAt ? 'gray' : 'brand',
      message: saved.unsubscribedAt
        ? `${saved.email} marked unsubscribed.`
        : `${saved.email} put back on the list.`,
    });
  }

  const header = (
    <PageHeader
      title="Signups"
      description="Addresses left on the coming-soon page, waiting to hear when Fluently opens."
      actions={
        <div className={classes.controls}>
          <Stack gap={4}>
            <Text size="sm" fw={500}>
              Show
            </Text>
            <SegmentedControl
              size="sm"
              value={status}
              onChange={(value) => {
                setStatus(value as WaitlistFilter);
                // A filter change means the old page number is about a
                // different list, and page 9 of a shorter one is empty.
                setPage(1);
              }}
              data={FILTERS}
            />
          </Stack>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              setQuery(term);
              setPage(1);
            }}
          >
            <TextInput
              size="sm"
              w={220}
              label="Search"
              placeholder="Email address"
              value={term}
              onChange={(event) => setTerm(event.currentTarget.value)}
              leftSection={<IconSearch size={15} stroke={1.7} />}
            />
          </form>
        </div>
      }
    />
  );

  const tiles = (
    <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
      <StatTile
        label="On the list"
        value={summary ? format.format(summary.total) : '—'}
        note="Every row, including people who have unsubscribed. The table below opens on subscribed only, so these two numbers are not meant to match."
      />
      <StatTile
        label="Joined in the last 7 days"
        value={summary ? format.format(summary.last7Days) : '—'}
        note="Counted from the join date on the server clock. A resubmitted address does not create a second row, so this counts new people rather than submissions."
      />
    </SimpleGrid>
  );

  if (isLoading && data === null) {
    return (
      <Stack gap="lg" className={classes.page}>
        {header}
        {tiles}
        <Center py="xl">
          <Loader size="sm" />
        </Center>
      </Stack>
    );
  }

  if (loadError !== null) {
    return (
      <Stack gap="lg" className={classes.page}>
        {header}
        {tiles}
        <Alert
          color="red"
          title="Cannot load the waitlist"
          icon={<IconAlertTriangle size={18} />}
        >
          <Text size="sm">{describeError(loadError)}</Text>
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack gap="lg" className={classes.page}>
      {header}
      {tiles}

      {signups.length === 0 ? (
        <Card>
          <Stack gap="xs" align="center" py="xl">
            <IconMailbox size={28} stroke={1.4} />
            <Text fw={600}>
              {query ? 'Nobody matches that search' : 'Nobody on this list yet'}
            </Text>
            <Text size="sm" c="dimmed" ta="center" maw={440}>
              {query
                ? 'Search matches an email address, which is the only thing recorded here.'
                : 'A row appears the first time somebody submits the form on tryfluently.com.'}
            </Text>
          </Stack>
        </Card>
      ) : (
        <Card p={0}>
          <Table.ScrollContainer minWidth={760}>
            <Table verticalSpacing="sm" horizontalSpacing="lg">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Address</Table.Th>
                  <Table.Th w={120}>Source</Table.Th>
                  <Table.Th w={190}>Status</Table.Th>
                  <Table.Th w={130}>Joined</Table.Th>
                  <Table.Th w={90} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {signups.map((signup) => {
                  const unsubscribed = signup.unsubscribedAt !== null;

                  return (
                    <Table.Tr key={signup.id}>
                      <Table.Td>
                        <Text size="sm" fw={500} className={classes.address}>
                          {signup.email}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge variant="light" color="gray" size="sm">
                          {signup.source}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        {/* Two badges is a legitimate state: notified is
                            orthogonal to consent, not a fourth status. */}
                        <Group gap={4} wrap="wrap">
                          <Badge
                            variant="light"
                            color={unsubscribed ? 'gray' : 'brand'}
                            size="sm"
                          >
                            {unsubscribed ? 'Unsubscribed' : 'Subscribed'}
                          </Badge>
                          {signup.notifiedAt && (
                            <Badge variant="light" color="teal" size="sm">
                              Notified
                            </Badge>
                          )}
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{on(signup.createdAt)}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Group gap={4} justify="flex-end" wrap="nowrap">
                          <ActionIcon
                            variant="subtle"
                            color="gray"
                            aria-label={
                              unsubscribed
                                ? `Put ${signup.email} back on the list`
                                : `Mark ${signup.email} unsubscribed`
                            }
                            onClick={() => openConsent(signup)}
                          >
                            {unsubscribed ? (
                              <IconMailPlus size={16} stroke={1.7} />
                            ) : (
                              <IconMailOff size={16} stroke={1.7} />
                            )}
                          </ActionIcon>
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            aria-label={`Remove ${signup.email}`}
                            onClick={() => openRemove(signup)}
                          >
                            <IconTrash size={16} stroke={1.7} />
                          </ActionIcon>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Card>
      )}

      <Group justify="space-between" wrap="wrap" gap="sm">
        <Text size="xs" c="dimmed">
          {/* The previous page stays on screen while a refetch is in flight —
              a table that empties and refills reads as a bug — so this is the
              only thing saying a request is running. */}
          {isLoading && (
            <Loader size={12} mr={6} style={{ verticalAlign: 'middle' }} />
          )}
          {format.format(total)} {total === 1 ? 'address' : 'addresses'}
          {status !== 'all' && ' matching this filter'}.
        </Text>
        {pages > 1 && (
          <Pagination
            size="sm"
            value={page}
            onChange={setPage}
            total={pages}
            withEdges
          />
        )}
      </Group>

      <Text size="xs" c="dimmed">
        These are addresses, not users — nobody here has an account, and the only
        handle they have on their own row is an unsubscribe link that does not
        exist yet. What you change here is recorded in the admin audit log. What
        you read is not.
      </Text>

      <Modal
        opened={isRemoveOpen}
        onClose={() => setRemoveOpen(false)}
        centered
        title={`Remove ${removing?.email ?? 'signup'}?`}
      >
        {removing && (
          <RemoveForm
            key={removing.id}
            signup={removing}
            onClose={() => setRemoveOpen(false)}
            onDone={onRemoved}
          />
        )}
      </Modal>

      <Modal
        opened={isConsentOpen}
        onClose={() => setConsentOpen(false)}
        centered
        title={
          consenting?.unsubscribedAt
            ? `Put ${consenting.email} back on the list?`
            : `Mark ${consenting?.email ?? 'signup'} unsubscribed?`
        }
      >
        {consenting && (
          <ConsentForm
            key={consenting.id}
            signup={consenting}
            onClose={() => setConsentOpen(false)}
            onDone={onConsentChanged}
          />
        )}
      </Modal>
    </Stack>
  );
}
